import { useState, useCallback } from 'react'
import { Plus, Search, FolderOpen, ChevronRight, Menu, CheckSquare, Square } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { DocumentoCard } from './DocumentoCard'
import { CarpetasSidebar } from './CarpetasSidebar'
import { SubirDocumentoDialog } from './SubirDocumentoDialog'
import { EditarDocumentoDialog } from './EditarDocumentoDialog'
import { DocumentoPreviewDialog } from './DocumentoPreviewDialog'
import { BulkActionsBar } from './BulkActionsBar'
import {
  useDocumentosCliente,
  useEliminarDocumento,
  useDescargarDocumento,
  CATEGORIAS_DOCUMENTO,
} from '@/core/hooks/useDocumentos'
import { toast } from 'sonner'
import { handleMutationError } from '@/lib/permisos-guard'
import { usePermissions } from '@/hooks/usePermissions'
import type { Documento, CategoriaDocumento } from '@/types/database'

interface DocumentosExplorerProps {
  clienteId: string
  clienteNombre: string
}

export function DocumentosExplorer({
  clienteId,
  clienteNombre,
}: DocumentosExplorerProps) {
  const { puede } = usePermissions()
  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState<CategoriaDocumento | null>(null)
  const [busqueda, setBusqueda] = useState('')
  const [dialogSubirOpen, setDialogSubirOpen] = useState(false)
  const [dialogEditarOpen, setDialogEditarOpen] = useState(false)
  const [dialogPreviewOpen, setDialogPreviewOpen] = useState(false)
  const [documentoEditar, setDocumentoEditar] = useState<Documento | null>(null)
  const [documentoPreview, setDocumentoPreview] = useState<Documento | null>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Estado de selección múltiple
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isDeleting, setIsDeleting] = useState(false)

  // Query de documentos
  const {
    data: documentos = [],
    isLoading,
    refetch,
  } = useDocumentosCliente(clienteId, categoriaSeleccionada ?? undefined)

  const eliminarDocumento = useEliminarDocumento()
  const descargarDocumento = useDescargarDocumento()

  // Filtrar por búsqueda local
  const documentosFiltrados = documentos.filter((doc) => {
    if (!busqueda) return true
    const busquedaLower = busqueda.toLowerCase()
    return (
      doc.nombre.toLowerCase().includes(busquedaLower) ||
      doc.nombre_original.toLowerCase().includes(busquedaLower)
    )
  })

  const handleVer = useCallback((documento: Documento) => {
    setDocumentoPreview(documento)
    setDialogPreviewOpen(true)
  }, [])

  const handleDescargar = useCallback(
    async (documento: Documento) => {
      try {
        await descargarDocumento.mutateAsync({
          path: documento.archivo_path,
          nombreArchivo: documento.nombre_original,
        })
        toast.success('Descarga iniciada')
      } catch (error) {
        console.error('Error al descargar:', error)
        toast.error('Error al descargar el documento')
      }
    },
    [descargarDocumento]
  )

  const handleEditar = useCallback((documento: Documento) => {
    setDocumentoEditar(documento)
    setDialogEditarOpen(true)
  }, [])

  const handleEliminar = useCallback(
    async (documento: Documento) => {
      try {
        await eliminarDocumento.mutateAsync(documento.id)
        toast.success('Documento eliminado')
      } catch (error) {
        handleMutationError(error, 'Error al eliminar el documento')
      }
    },
    [eliminarDocumento]
  )

  const handleCategoriaChange = (cat: CategoriaDocumento | null) => {
    setCategoriaSeleccionada(cat)
    setBusqueda('')
    setMobileMenuOpen(false)
    // Limpiar selección al cambiar categoría
    setSelectedIds(new Set())
  }

  // Handlers de selección múltiple
  const toggleSelectionMode = useCallback(() => {
    setSelectionMode((prev) => !prev)
    setSelectedIds(new Set())
  }, [])

  const handleSelectionChange = useCallback((id: string, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (selected) {
        next.add(id)
      } else {
        next.delete(id)
      }
      return next
    })
  }, [])

  const handleSelectAll = useCallback(() => {
    setSelectedIds(new Set(documentosFiltrados.map((d) => d.id)))
  }, [documentosFiltrados])

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set())
    setSelectionMode(false)
  }, [])

  const handleDeleteSelected = useCallback(async () => {
    const idsToDelete = Array.from(selectedIds)
    if (idsToDelete.length === 0) return

    setIsDeleting(true)
    let successCount = 0
    let errorCount = 0

    for (const id of idsToDelete) {
      try {
        await eliminarDocumento.mutateAsync(id)
        successCount++
      } catch (error) {
        console.error(`Error eliminando documento ${id}:`, error)
        errorCount++
      }
    }

    setIsDeleting(false)
    setSelectedIds(new Set())
    setSelectionMode(false)

    if (errorCount === 0) {
      toast.success(
        successCount === 1
          ? 'Documento eliminado'
          : `${successCount} documentos eliminados`
      )
    } else {
      toast.error(
        `Se eliminaron ${successCount} documentos, pero ${errorCount} fallaron`
      )
    }
  }, [selectedIds, eliminarDocumento])

  const categoriaLabel = categoriaSeleccionada
    ? CATEGORIAS_DOCUMENTO[categoriaSeleccionada]?.label ?? categoriaSeleccionada
    : 'Todos los documentos'

  return (
    <>
      <div className="flex h-full gap-0 border rounded-lg overflow-hidden bg-background">
        {/* Sidebar de carpetas - Desktop */}
        <div className="hidden lg:flex w-64 border-r bg-muted/30 shrink-0">
          <CarpetasSidebar
            clienteId={clienteId}
            categoriaSeleccionada={categoriaSeleccionada}
            onCategoriaChange={handleCategoriaChange}
          />
        </div>

        {/* Contenido principal */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header con breadcrumb */}
          <div className="flex items-center gap-3 p-4 border-b bg-background">
            {/* Botón menú mobile */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden shrink-0">
                  <Menu className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                <SheetHeader className="sr-only">
                  <SheetTitle>Carpetas</SheetTitle>
                </SheetHeader>
                <CarpetasSidebar
                  clienteId={clienteId}
                  categoriaSeleccionada={categoriaSeleccionada}
                  onCategoriaChange={handleCategoriaChange}
                />
              </SheetContent>
            </Sheet>

            {/* Breadcrumb */}
            <div className="flex items-center gap-1 text-sm flex-1 min-w-0">
              <span className="font-medium truncate">{clienteNombre}</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-muted-foreground truncate">{categoriaLabel}</span>
            </div>

            {/* Acciones */}
            <div className="flex items-center gap-2">
              {documentosFiltrados.length > 0 && (
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={selectionMode ? 'secondary' : 'outline'}
                        size="icon"
                        onClick={toggleSelectionMode}
                        className="h-9 w-9"
                      >
                        {selectionMode ? (
                          <CheckSquare className="h-4 w-4" />
                        ) : (
                          <Square className="h-4 w-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {selectionMode ? 'Cancelar selección' : 'Seleccionar múltiples'}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <Button onClick={() => setDialogSubirOpen(true)} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Subir</span>
              </Button>
            </div>
          </div>

          {/* Barra de búsqueda */}
          <div className="p-4 border-b">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar documentos..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {/* Barra de acciones bulk */}
          {selectionMode && selectedIds.size > 0 && (
            <div className="px-4 pt-4">
              <BulkActionsBar
                selectedCount={selectedIds.size}
                totalCount={documentosFiltrados.length}
                onSelectAll={handleSelectAll}
                onClearSelection={handleClearSelection}
                onDeleteSelected={handleDeleteSelected}
                isDeleting={isDeleting}
              />
            </div>
          )}

          {/* Lista de documentos */}
          <div className="flex-1 overflow-y-auto p-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : documentosFiltrados.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <FolderOpen className="h-16 w-16 mb-4 opacity-50" />
                {busqueda ? (
                  <>
                    <p className="font-medium text-lg">No se encontraron documentos</p>
                    <p className="text-sm">Intenta con otra búsqueda</p>
                  </>
                ) : (
                  <>
                    <p className="font-medium text-lg">
                      {categoriaSeleccionada
                        ? `No hay documentos en ${categoriaLabel}`
                        : 'No hay documentos'}
                    </p>
                    <p className="text-sm mb-4">Sube el primer documento para comenzar</p>
                    <Button onClick={() => setDialogSubirOpen(true)} variant="outline">
                      <Plus className="h-4 w-4 mr-2" />
                      Subir documento
                    </Button>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {documentosFiltrados.length} documento
                  {documentosFiltrados.length !== 1 ? 's' : ''}
                </p>
                {documentosFiltrados.map((documento) => (
                  <DocumentoCard
                    key={documento.id}
                    documento={documento}
                    onVer={selectionMode ? undefined : handleVer}
                    onDescargar={selectionMode ? undefined : handleDescargar}
                    onEditar={selectionMode || !puede.editarDocumento ? undefined : handleEditar}
                    onEliminar={selectionMode || !puede.eliminarDocumento ? undefined : handleEliminar}
                    showCliente={false}
                    selectable={selectionMode}
                    selected={selectedIds.has(documento.id)}
                    onSelectionChange={(selected) => handleSelectionChange(documento.id, selected)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <SubirDocumentoDialog
        open={dialogSubirOpen}
        onOpenChange={setDialogSubirOpen}
        clienteId={clienteId}
        clienteNombre={clienteNombre}
        onSuccess={() => refetch()}
      />

      <EditarDocumentoDialog
        open={dialogEditarOpen}
        onOpenChange={setDialogEditarOpen}
        documento={documentoEditar}
        onSuccess={() => {
          setDocumentoEditar(null)
          refetch()
        }}
      />

      <DocumentoPreviewDialog
        documento={documentoPreview}
        open={dialogPreviewOpen}
        onOpenChange={(open) => {
          setDialogPreviewOpen(open)
          if (!open) setDocumentoPreview(null)
        }}
      />
    </>
  )
}
